import { create } from 'zustand';
import type { WalletDto, WalletEntryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * A customer's store credit (ADR-081): the balance, its ledger paged by
 * cursor, and a change made by hand with a reason. Its own store — its own
 * endpoints, and a money decision with its own permission.
 */

interface WalletState {
  customerId: string | null;
  wallet: WalletDto | null;
  entries: WalletEntryDto[];
  nextCursor: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: (customerId: string) => Promise<void>;
  loadMore: () => Promise<void>;
  adjust: (customerId: string, amount: number, note: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useWalletStore = create<WalletState>((set, get) => ({
  customerId: null,
  wallet: null,
  entries: [],
  nextCursor: null,
  loading: false,
  saving: false,
  error: null,

  async load(customerId) {
    set({ customerId, wallet: null, entries: [], nextCursor: null, loading: true, error: null });
    try {
      const [wallet, page] = await Promise.all([
        withSession(() => adminApi.wallet.get(customerId)),
        withSession(() => adminApi.wallet.entries(customerId, { limit: PAGE_SIZE })),
      ]);
      // Another customer was opened while this one loaded.
      if (get().customerId !== customerId) return;
      set({ wallet, entries: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the store credit.') });
    }
  },

  async loadMore() {
    const { customerId, nextCursor, loading } = get();
    if (!customerId || !nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() => adminApi.wallet.entries(customerId, { limit: PAGE_SIZE, cursor: nextCursor }));
      set((state) => ({ entries: [...state.entries, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more movements.') });
    }
  },

  async adjust(customerId, amount, note) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.wallet.adjust(customerId, { amount, note }));
      set({ saving: false });
      // The new line heads the ledger, with the balance after it.
      await get().load(customerId);
      return true;
    } catch (error) {
      // Taking more than is there comes back from the API in words.
      set({ saving: false, error: errorMessage(error, 'Could not change the store credit.') });
      return false;
    }
  },
}));
